import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'cansibei');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const category = categories.find((c) => c.slug === activeCategory) || categories[0];
  const articles = articlesByCategory(activeCategory);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 被护专家`,
    description: category.description,
    url: `https://beiziganxi.com/category/${activeCategory}`,
  };

  return (
    <>
      <SEOHead
        title={`${category.name} | 专业干洗护理指南`}
        description={category.description}
        keywords={`${category.name},被子干洗,被芯清洁,专业洗被服务`}
        canonicalPath={`/category/${activeCategory}`}
        schema={schema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <section className="bg-background-100 py-10">
          <div className="container-main">
            <div className="relative rounded-2xl overflow-hidden h-48 md:h-64 mb-8">
              <img
                src={category.image}
                alt={category.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
                <div className="p-8 md:p-12">
                  <h1 className="text-2xl md:text-4xl font-bold text-white font-heading mb-2">
                    {category.name}
                  </h1>
                  <p className="text-sm text-white/80 max-w-md leading-relaxed">
                    {category.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 no-underline ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles List */}
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200 no-underline cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <h2 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors font-heading">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{article.date}</span>
                        <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-foreground-500">
                <i className="ri-inbox-line text-4xl mb-4 block"></i>
                <p className="text-sm">该分类暂无文章，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}